import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star } from 'lucide-react';

const allTestimonials = [
  {
    category: 'owners',
    style: 'chat',
    company: 'Northline Digital',
    author: 'Agency Owner',
    time: '8:12 AM',
    text: 'HireX8 helped us ship 3 full client campaigns in the same month without adding internal headcount.',
  },
  {
    category: 'owners',
    style: 'review',
    company: 'Peakframe Studio',
    author: 'Taylor Singh — Agency Principal',
    text: 'We used HireX8 to handle overflow and ended up increasing monthly project capacity by 40%.',
    rating: 5,
  },
  {
    category: 'owners',
    style: 'email',
    company: 'Monarch Digital Works',
    author: 'Founder',
    subject: '5-star delivery feedback',
    text: 'We accepted bigger contracts because we trusted delivery support would keep pace with demand.',
    time: '7:06 PM',
  },
  {
    category: 'owners',
    style: 'chat',
    company: 'Proofline Studio',
    author: 'Co-Owner',
    time: '3:27 PM',
    text: 'We launched two net-new service lines this year because we finally had enough production bandwidth.',
  },
  {
    category: 'owners',
    style: 'review',
    company: 'Brightlake Interactive',
    author: 'Blake Austin — Founder & Agency Lead',
    text: 'We moved from constantly firefighting to confidently planning because execution became predictable.',
    rating: 5,
  },
  {
    category: 'owners',
    style: 'chat',
    company: 'Scale Avenue Agency',
    author: 'CEO',
    time: '9:45 AM',
    text: 'The AI-accelerated workflows gave us real margin improvement without lowering quality.',
  },
  {
    category: 'pms',
    style: 'chat',
    company: 'Signal Foundry',
    author: 'Senior Project Manager',
    time: '9:45 AM',
    text: 'Our sprint boards stay healthy now because blockers get resolved in hours instead of days.',
  },
  {
    category: 'pms',
    style: 'review',
    company: 'Vista Growth Lab',
    author: 'Sofia Graham — Web Project Manager',
    text: 'We shipped a complex web relaunch on time with fewer escalation calls than any recent project.',
    rating: 5,
  },
  {
    category: 'pms',
    style: 'email',
    company: 'Lighthouse Creative',
    author: 'Client Delivery Manager — Ethan Wells',
    subject: '5-star delivery feedback',
    text: 'Clients love the pace and I love that deadlines are finally realistic even with changing scope.',
    time: '11:03 AM',
  },
  {
    category: 'pms',
    style: 'chat',
    company: 'Matterlane Digital',
    author: 'Client Success Project Manager',
    time: '1:18 PM',
    text: 'I can forecast delivery with confidence now, which has made client communication much easier.',
  },
  {
    category: 'pms',
    style: 'review',
    company: 'Summit Orbit',
    author: 'Natalie Ross — Creative Project Manager',
    text: 'Review cycles are tighter now because first drafts arrive so much closer to brief expectations.',
    rating: 5,
  },
  {
    category: 'pms',
    style: 'chat',
    company: 'Orbit Click Media',
    author: 'Senior Delivery PM',
    time: '11:03 AM',
    text: 'The throughput increase has been huge, but what impressed me most is reliability week after week.',
  },
];

const tabs = [
  { key: 'all', label: 'All Reviews' },
  { key: 'owners', label: 'Agency Owners' },
  { key: 'pms', label: 'Project Managers' },
];

const ChatBubble = ({ t }) => (
  <div className="testimonial-card">
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-full bg-apple-surface-2 flex items-center justify-center text-[11px] font-bold text-foreground">
          {t.company.charAt(0)}
        </div>
        <div>
          <p className="text-[13px] font-semibold text-foreground leading-tight">{t.company}</p>
          <p className="text-[11px] text-muted-foreground">{t.author}</p>
        </div>
      </div>
      <span className="text-[11px] text-muted-foreground">{t.time}</span>
    </div>
    <div className="bg-apple-surface rounded-xl rounded-tl-sm px-4 py-3">
      <p className="text-[14px] text-foreground leading-[1.55]">"{t.text}"</p>
    </div>
  </div>
);

const EmailCard = ({ t }) => (
  <div className="testimonial-card">
    <div className="text-[10px] font-semibold uppercase tracking-[0.1em] text-muted-foreground mb-3">{t.company}</div>
    <div className="space-y-1 mb-3 text-[12px] text-muted-foreground">
      <p><span className="font-semibold">From:</span> {t.author}</p>
      <p><span className="font-semibold">Subject:</span> {t.subject}</p>
      <p className="text-right">{t.time}</p>
    </div>
    <div className="border-t border-border pt-3">
      <p className="text-[14px] text-foreground leading-[1.55]">"{t.text}"</p>
    </div>
  </div>
);

const ReviewCard = ({ t }) => (
  <div className="testimonial-card">
    <div className="flex items-center gap-1 mb-3">
      {[...Array(t.rating)].map((_, i) => (
        <Star key={i} size={13} className="fill-yellow-400 text-yellow-400" />
      ))}
    </div>
    <p className="text-[14px] text-foreground leading-[1.55] mb-4">"{t.text}"</p>
    <div className="border-t border-border pt-3 flex items-center justify-between">
      <div>
        <p className="text-[13px] font-semibold text-foreground">{t.author}</p>
        <p className="text-[11px] text-muted-foreground">{t.company}</p>
      </div>
      <span className="text-[10px] font-semibold uppercase tracking-[0.1em] px-2 py-1 rounded-full bg-apple-surface text-muted-foreground">
        Verified
      </span>
    </div>
  </div>
);

const TestimonialCard = ({ t }) => {
  if (t.style === 'chat') return <ChatBubble t={t} />;
  if (t.style === 'email') return <EmailCard t={t} />;
  return <ReviewCard t={t} />;
};

export const TestimonialsSection = () => {
  const [active, setActive] = useState('all');
  const filtered = active === 'all' ? allTestimonials : allTestimonials.filter(t => t.category === active);

  return (
    <section id="testimonials" className="py-24 md:py-32 bg-background border-t border-border">
      <div className="section-container">
        {/* Header */}
        <motion.div
          className="mb-10"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <p className="section-label mb-5">5-Star Testimonials</p>
          <div className="flex flex-col md:flex-row md:items-end gap-5 mb-8">
            <h2 className="text-[42px] sm:text-[50px] font-extrabold tracking-tighter text-foreground leading-none">
              42 Real 5-Star Reviews.
            </h2>
            <p className="text-[17px] text-muted-foreground leading-[1.5] md:pb-1 max-w-sm">
              From agency owners and project managers across design, SEO, development, and content.
            </p>
          </div>

          {/* Tab filter */}
          <div className="flex items-center gap-2 flex-wrap">
            {tabs.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActive(tab.key)}
                className={`px-4 py-2 rounded-full text-[13px] font-medium transition-all duration-150 border ${
                  active === tab.key
                    ? 'bg-foreground text-background border-foreground'
                    : 'bg-background text-muted-foreground border-border hover:border-foreground/30'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Grid */}
        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.35 }}
          >
            {filtered.slice(0, 9).map((t, i) => (
              <motion.div
                key={`${t.company}-${i}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05, duration: 0.4 }}
              >
                <TestimonialCard t={t} />
              </motion.div>
            ))}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};
